import { useEffect, useMemo, useState } from 'react';
import type {
  DropDownListViewModel,
  DropDownRepositoryResponse,
  ImportExamItemGridItemRepositoryResponse,
} from '@/shared/api/generated';
import {
  useGetApiV1ExamAuthorDropdown,
  useGetApiV1ExamCompanyDropdown,
  useGetApiV1ExamComponentTypes,
  useGetApiV1ExamDisciplineDropdownFiltered,
  useGetApiV1ExamExamlangidImportExamItemGrid,
} from '@/shared/api/generated';

// ─── Tipos ────────────────────────────────────────────────────────────────────

/** Filtros da busca de questões no contexto do wizard */
export interface ExamQuestionSelectionFilters {
  readonly disciplineId: number | null;
  readonly personId: number | null;
  readonly companyId: number | null;
  readonly componentType: number | null;
  readonly cultureInfo: string;
  readonly labelSearch: string;
  readonly startDate: string;
  readonly endDate: string;
  readonly page: number;
}

/** Questão selecionável — subconjunto de campos necessários no wizard */
export interface SelectableQuestion {
  readonly id: string;
  readonly text: string;
  readonly discipline: string;
  readonly author: string;
  readonly type: string;
  readonly status: string;
  readonly isActive: boolean;
  /** Idioma da questão — herdado do filtro ativo no momento da seleção */
  readonly cultureInfo: string;
}

// ─── Constantes ───────────────────────────────────────────────────────────────

const PAGE_SIZE = 20;

function makeDefaultFilters(): ExamQuestionSelectionFilters {
  return {
    disciplineId: null,
    personId: null,
    companyId: null,
    componentType: null,
    cultureInfo: 'pt-BR',
    labelSearch: '',
    startDate: '',
    endDate: '',
    page: 1,
  };
}

// ─── Helper ───────────────────────────────────────────────────────────────────

function mapToSelectable(
  item: ImportExamItemGridItemRepositoryResponse,
  cultureInfo: string,
): SelectableQuestion {
  return {
    id: String(item.id ?? ''),
    text: item.label ?? '',
    discipline: item.disciplineName ?? '',
    author: item.personAuthorName ?? '',
    type: item.componentType ?? '',
    status: item.active ? 'Ativo' : 'Inativo',
    isActive: item.active ?? false,
    cultureInfo,
  };
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Hook para seleção de questões disponíveis para importar em um idioma de prova.
 *
 * Gerencia filtros locais e o conjunto de questões selecionadas, consumindo o
 * endpoint específico por `examLangId` que já filtra questões compatíveis com
 * o tipo da prova.
 *
 * @param examLangId - ID do ExamLang destino (usado como path param no GET)
 *
 * @example
 * ```tsx
 * const { data, selectedIds, toggleQuestion } = useExamQuestionSelection(examLangId);
 * ```
 */
export function useExamQuestionSelection(
  examLangId: number,
  examCultureInfos: string[] = [],
  initialCultureInfo?: string,
) {
  const [filters, setFilters] = useState<ExamQuestionSelectionFilters>(() => ({
    ...makeDefaultFilters(),
    ...(initialCultureInfo ? { cultureInfo: initialCultureInfo } : {}),
  }));

  const [disciplineSearch, setDisciplineSearch] = useState('');
  const [debouncedDisciplineFilter, setDebouncedDisciplineFilter] = useState('');

  useEffect(() => {
    if (disciplineSearch === '') {
      setDebouncedDisciplineFilter('');
      return;
    }
    const timer = setTimeout(() => setDebouncedDisciplineFilter(disciplineSearch), 1000);
    return () => clearTimeout(timer);
  }, [disciplineSearch]);

  const isDisciplineReady = debouncedDisciplineFilter.length >= 3;

  /** Seleções por idioma: { 'pt-BR': [...questões], 'en-US': [...] } */
  const [selectionMap, setSelectionMap] = useState<Record<string, SelectableQuestion[]>>({});

  const currentCulture = filters.cultureInfo;
  const currentSelected = selectionMap[currentCulture] ?? [];
  const selectedIds: ReadonlySet<string> = new Set(currentSelected.map(q => q.id));

  /** Todas as questões selecionadas em todos os idiomas (lista plana com cultureInfo) */
  const allSelectedQuestions = useMemo(() => Object.values(selectionMap).flat(), [selectionMap]);

  /** Contagem de questões selecionadas por idioma */
  const selectionCounts: Record<string, number> = Object.fromEntries(
    examCultureInfos.map(lang => [lang, selectionMap[lang]?.length ?? 0]),
  );

  /** True quando todos os idiomas configurados têm o mesmo número de questões (ou nenhum tem) */
  const nonZeroCounts = Object.values(selectionCounts).filter(c => c > 0);
  const isSelectionBalanced =
    examCultureInfos.length === 0 ||
    nonZeroCounts.length === 0 ||
    (nonZeroCounts.every(c => c === nonZeroCounts[0]) &&
      nonZeroCounts.length === examCultureInfos.length);

  const queryParams = {
    PageNumber: filters.page,
    PageSize: PAGE_SIZE,
    ...(filters.disciplineId !== null ? { DisciplineId: filters.disciplineId } : {}),
    ...(filters.personId !== null ? { AuthorId: filters.personId } : {}),
    ...(filters.companyId !== null ? { CompanyId: filters.companyId } : {}),
    ...(filters.componentType !== null ? { ComponentType: filters.componentType } : {}),
    CultureInfo: filters.cultureInfo,
    ...(filters.labelSearch ? { LabelSearch: filters.labelSearch } : {}),
    ...(filters.startDate ? { StartDate: filters.startDate } : {}),
    ...(filters.endDate ? { EndDate: filters.endDate } : {}),
  };

  const query = useGetApiV1ExamExamlangidImportExamItemGrid(examLangId, queryParams);
  const authorsQuery = useGetApiV1ExamAuthorDropdown();
  const companiesQuery = useGetApiV1ExamCompanyDropdown();
  const componentTypesQuery = useGetApiV1ExamComponentTypes();
  const disciplinesQuery = useGetApiV1ExamDisciplineDropdownFiltered(
    isDisciplineReady ? { filter: debouncedDisciplineFilter } : undefined,
    { query: { enabled: isDisciplineReady } },
  );

  const rawData = query.data?.data ?? [];
  const data: SelectableQuestion[] = rawData.map(item => mapToSelectable(item, currentCulture));

  const totalCount = query.data?.totalCount ?? 0;
  const totalPages = query.data?.totalPages ?? 1;
  const currentPage = query.data?.currentPage ?? filters.page;

  function updateFilters(partial: Partial<ExamQuestionSelectionFilters>) {
    setFilters(prev => ({
      ...prev,
      ...partial,
      page: 'page' in partial ? (partial.page ?? 1) : 1,
    }));
  }

  function resetFilters() {
    setFilters(makeDefaultFilters());
  }

  function toggleQuestion(question: SelectableQuestion): SelectableQuestion[] {
    const culture = question.cultureInfo;
    const current = selectionMap[culture] ?? [];
    const isSelected = current.some(q => q.id === question.id);
    const next = isSelected ? current.filter(q => q.id !== question.id) : [...current, question];

    setSelectionMap(prev => ({ ...prev, [culture]: next }));
    return next;
  }

  return {
    data,
    isLoading: query.isPending,
    totalCount,
    currentPage,
    totalPages,
    filters,
    selectedIds,
    selectedQuestions: currentSelected,
    allSelectedQuestions,
    selectionCounts,
    isSelectionBalanced,
    authorOptions: (authorsQuery.data ?? []) as DropDownRepositoryResponse[],
    companyOptions: (companiesQuery.data ?? []) as DropDownRepositoryResponse[],
    componentTypeOptions: (componentTypesQuery.data ?? []) as DropDownListViewModel[],
    disciplineOptions: (disciplinesQuery.data ?? []) as DropDownRepositoryResponse[],
    isDisciplineQuerySuccess: disciplinesQuery.isSuccess,
    updateFilters,
    resetFilters,
    toggleQuestion,
    setDisciplineSearch,
  } as const;
}
